import { useMemo } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import P2 from '@base/P2/P2';
import H6 from '@base/H6/H6';
import Link from '@base/Link/Link';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { pluralize } from '@utils/textHelper';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import { Spacings, SOLID_BORDER_STYLE, breakpoints } from '@styles/index';
import {
  BATCH_SYLLABUS_VIEWED,
  BATCH_KEYS,
  SYLLABUS_CARD_CLICKED,
  SYLLABUS_CLICKED_KEYS,
  SEARCH_RESULT_CLICKED,
  SEARCH_RESULT_PAGE
} from '@constants/segment';
import {
  topologyLevels,
  evtNameCategoryMap,
  itemTypes
} from '@constants/index';
import { segment } from '@actions/analytics';
import { getLinkPath, getTopicLink, getTopologyName } from '@models/Topology';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';

const Title = styled(H6)`
  margin-bottom: ${Spacings.SPACING_1B};
  color: var(--color-text-primary);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
  }
`;

const TopicGroupContainer = styled.div`
  display: grid;
  grid-template-columns: ${({ $gridCols }) => $gridCols};
  grid-column-gap: ${({ $colGap }) => $colGap};
  width: 100%;
`;

const CoursesInfo = styled(P2)`
  display: flex;
  align-items: center;
  grid-row: 2 / 3;
  grid-column: 1 / 2;
`;

const TopicGroupCard = ({
  topology,
  goal,
  color,
  gridColumn,
  className,
  courseType,
  batch,
  topicUID,
  disabled,
  renderProgress,
  renderCourses,
  hideIfNoCourses,
  dataAnalytics,
  index,
  titleTag,
  updateRecentSearchLog,
  searchData
}) => {
  const dispatch = useDispatch();
  const { uid: topologyUID, level = 30, name } = topology;
  const { selectedGoal } = useSelectedGoal();
  const goalSegmentData = useGoalEventProps({ goalUID: batch?.goal?.uid });
  const segmentData = useAllTypesOfCourseEventProps({
    course: batch,
    isBatch: true
  });
  const meUID = useMyInfo(getMeUid);
  const anchorStyle = `
    height: ${Spacings.SPACING_24B};
    box-shadow: ${boxShadowGenerator()};
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_3B}
    ${Spacings.SPACING_6B} ${Spacings.SPACING_8B};
    background-color: var(--color-base-1);
    border-radius: ${Spacings.SPACING_2B};
    border-left: ${Spacings.SPACING_2B} ${SOLID_BORDER_STYLE}
      ${color};
    grid-column: span ${gridColumn};
    cursor: pointer;
    :hover {
      box-shadow: ${boxShadowGenerator({
        yOffset: Spacings.SPACING_1B,
        blur: Spacings.SPACING_2B
      })};
    }
    transition: box-shadow 0.2s ease-in-out;
    display: flex;
    align-items: center;
    @media only screen and (max-width: ${breakpoints.mobile}) {
      margin-bottom: 16px;
      &.last {
        margin-bottom: 56px;
      }
    }
    `;
  const isBatch = itemTypes.BATCHES.includes(courseType);
  const getHref = () => {
    let href = null;
    if (courseType === 'freeCourse') {
      href =
        '/goal/[goalSlug]/[goalUID]/free-platform/[topicSlugType]/[topicSlugUID]';
      if (topology.level === topologyLevels.CONCEPT)
        href =
          '/goal/[goalSlug]/[goalUID]/free-platform/[topicSlugType]/[topicSlugUID]/[conceptUID]';
    } else if (isBatch) href = '/batch/[slug]/[batchUID]/topics/[topicUID]';
    else if (courseType === itemTypes.CONCEPT)
      href = '/batch/[slug]/[batchUID]/topics/[topicUID]/courses/[conceptUID]';
    else {
      href = getLinkPath();
      if (topology.level === topologyLevels.TOPIC) {
        href += '/courses';
      }
    }
    return href;
  };

  const getLink = () => {
    let link = null;
    const { permalink } = topology;

    if (courseType === 'freeCourse') {
      link = `/goal/${goal.slug}/${goal.uid}/free-platform/${topology.slug}/${topology.uid}`;
      if (topology.level === topologyLevels.CONCEPT) {
        const permalinkSplit = permalink.split('/');
        const parSlug = permalinkSplit[permalinkSplit.length - 3];
        link = `/goal/${goal.slug}/${goal.uid}/free-platform/${parSlug}/${topology.slug}/${topology.uid}`;
      }
    } else if (isBatch) {
      link = `/batch/${batch.slug}/${batch.uid}/topics/${topology.uid}`;
    } else if (courseType === itemTypes.CONCEPT) {
      link = `/batch/${batch.slug}/${batch.uid}/topics/${topicUID}/courses/${topology.uid}`;
    } else {
      link = getTopicLink(topology, goal);
      if (topology.level === topologyLevels.TOPIC) {
        link += '/courses';
      }
    }

    return link;
  };

  const handleAnalytics = () => {
    if (courseType === 'search') {
      const nSearchData = { ...searchData };
      delete nSearchData.query;
      const position = nSearchData.position ? nSearchData.position : 'See all';
      segment.track(
        SEARCH_RESULT_CLICKED,
        {
          ...goalSegmentData,
          sub_position: index + 1,
          last_primary_source: SEARCH_RESULT_PAGE,
          last_primary_source_section: 'Topic',
          search_section: 'Global',
          current_screen: 'Topic',
          eventName: SEARCH_RESULT_CLICKED,
          eventType: 'click',
          version: 2,
          objectID: `${searchData.object_type}:${topologyUID}`,
          userToken: meUID,
          ...nSearchData,
          position
        },
        evtNameCategoryMap.WITHOUT_CATEGORY
      );
      dispatch(
        updateRecentSearchLog({
          ...searchData,
          entity_id: `${searchData.object_type}:${topologyUID}`,
          goal_uid: selectedGoal.uid
        })
      );
    }
    const topologyName = getTopologyName[parseInt(level, 10)];
    const eventName = itemTypes.BATCHES.includes(courseType)
      ? BATCH_SYLLABUS_VIEWED
      : SYLLABUS_CARD_CLICKED;
    const eventKeys = isBatch ? BATCH_KEYS : SYLLABUS_CLICKED_KEYS;
    let eventData = {
      ...goalSegmentData
    };
    if (!isBatch) {
      eventData.topology_id = topologyUID;
      eventData.topology_level = topologyName;
      eventData.level = level;
      eventData.topology_name = name;
      eventData.start_date = batch.startsAt;
      eventData.end_date = batch.completedAt;
      eventData.batch_state = batch.state;
    } else {
      eventData = { ...eventData, ...segmentData };
    }

    segment.track(
      eventName,
      eventData,
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };

  const topologyCoursesCount = useMemo(() => {
    if (courseType === 'plus' || courseType === 'syllabus')
      return topology.plusCourseCount;
    return (
      topology.coursesCount || topology.courseCount || topology.course_count
    );
  }, [
    courseType,
    topology.plusCourseCount,
    topology.courseCount,
    topology.coursesCount,
    topology.course_count
  ]);

  const renderCoursesInfo = () => {
    if (renderCourses) {
      return renderCourses(topology, topologyCoursesCount);
    }

    return topologyCoursesCount
      ? pluralize(topologyCoursesCount, 'course')
      : 'Courses will be added soon';
  };

  return !hideIfNoCourses || topologyCoursesCount > 0 ? (
    <Link
      href={getHref()}
      as={getLink()}
      anchorStyle={anchorStyle}
      className={className}
      disabled={disabled}
      onClick={handleAnalytics}
    >
      <TopicGroupContainer
        data-analytics={`${dataAnalytics}-topic`}
        $colGap={
          renderProgress && topology.progress > 0 ? Spacings.SPACING_3B : '0px'
        }
        $gridCols={
          renderProgress && topology.progress > 0 ? '1fr max-content' : '1fr'
        }
      >
        <Title lineClamp={1} as={titleTag}>
          {topology.name}
        </Title>
        {renderProgress && renderProgress(topology)}
        {courseType !== 'concept' && (
          <CoursesInfo color="var(--color-text-primary)">
            {renderCoursesInfo()}
          </CoursesInfo>
        )}
      </TopicGroupContainer>
    </Link>
  ) : null;
};

export default TopicGroupCard;

TopicGroupCard.propTypes = {
  topology: PropTypes.objectOf(PropTypes.any).isRequired,
  goal: PropTypes.objectOf(PropTypes.any).isRequired,
  color: PropTypes.string.isRequired,
  gridColumn: PropTypes.number,
  className: PropTypes.string,
  courseType: PropTypes.string,
  batch: PropTypes.objectOf(PropTypes.any),
  topicUID: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  renderProgress: PropTypes.func,
  renderCourses: PropTypes.func,
  hideIfNoCourses: PropTypes.bool,
  dataAnalytics: PropTypes.string,
  index: PropTypes.number.isRequired,
  titleTag: PropTypes.string,
  updateRecentSearchLog: PropTypes.func,
  searchData: PropTypes.objectOf(PropTypes.any)
};

TopicGroupCard.defaultProps = {
  gridColumn: 6,
  className: '',
  courseType: null,
  batch: {},
  disabled: false,
  renderProgress: undefined,
  renderCourses: undefined,
  hideIfNoCourses: false,
  dataAnalytics: '',
  titleTag: 'h3',
  updateRecentSearchLog: () => {},
  searchData: {}
};
